import { useEffect, useState } from 'react'
import { getAppBearer } from '@/lib/sdk-session-token'
import { localCopyOf } from '@/lib/local-media-copies'
import {
  isPrivateAttachmentUrl,
  resolveAttachmentUrl,
  type ResolveOptions,
} from '../services/private-attachment'

/** Attachment URLs served by the inMeet backend, which are behind bearer auth. */
function needsAuth(url: string): boolean {
  return url.includes('/api/v1/attachments/')
}

/**
 * Turn an attachment URL into one an `<img>` or `<video>` can actually load.
 *
 * The local storage backend serves attachments from
 * `/api/v1/attachments/:id/raw`, and every route on that router sits behind
 * `requireAuth`. A media element cannot send an Authorization header — there is
 * no API for it — so the request goes out bare and comes back 401, which is
 * what a broken image, a broken video and a broken group avatar all were.
 *
 * Fetched here with this client's own token and handed back as an object URL.
 * A private chat file (VAPT 2026-10) is traded for a link signed for this user
 * (private-attachment.ts), which an element loads as it is. A public URL (the
 * S3 backend, or any outside link) passes straight through, so this costs
 * nothing where it is not needed. `opts.meetingId` resolves a meeting chat's
 * file from that meeting's record rather than the live room.
 */
export function useAttachmentSrc(url?: string | null, opts: ResolveOptions = {}): string | undefined {
  const meetingId = opts.meetingId
  const [resolved, setResolved] = useState<string | undefined>(
    localCopyOf(url) ?? (url && !needsAuth(url) && !isPrivateAttachmentUrl(url) ? url : undefined),
  )

  useEffect(() => {
    if (!url) {
      setResolved(undefined)
      return
    }
    // A picture this tab just sent: shown from the sender's own file.
    const local = localCopyOf(url)
    if (local) {
      setResolved(local)
      return
    }
    if (isPrivateAttachmentUrl(url)) {
      let cancelled = false
      setResolved(undefined)
      resolveAttachmentUrl(url, { meetingId }).then(
        (signed) => { if (!cancelled) setResolved(signed) },
        // Not this user's to see, or signed out: the caller's placeholder.
        () => { if (!cancelled) setResolved(undefined) },
      )
      return () => { cancelled = true }
    }
    if (!needsAuth(url)) {
      setResolved(url)
      return
    }

    let objectUrl: string | null = null
    const controller = new AbortController()

    void (async () => {
      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${getAppBearer()}` },
          signal: controller.signal,
        })
        if (!res.ok) throw new Error(String(res.status))
        objectUrl = URL.createObjectURL(await res.blob())
        setResolved(objectUrl)
      } catch {
        // Leave it undefined rather than handing back a URL that 401s: the
        // caller renders its placeholder, which is honest, instead of a broken
        // image icon.
        if (!controller.signal.aborted) setResolved(undefined)
      }
    })()

    return () => {
      controller.abort()
      // Revoked on unmount / url change — a thread of images would otherwise
      // hold every blob it ever rendered for the tab's lifetime.
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url, meetingId])

  return resolved
}
